import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Brain, Check, X, ChevronRight, Zap, Target, Sparkles } from "lucide-react";

const TIERS = [
  { name: "FREE", price: "$0", accent: "#A1A1AA", perks: ["1 scenario per day", "Beginner + Intermediate IQ levels", "Basic IQ score", "Answer explanations", "Basic drill recommendation"] },
  { name: "VIP", price: "$1/mo", accent: "#22D3EE", perks: ["10 scenarios per day", "Levels 1–3 (Advanced IQ)", "Daily Challenge · 2× XP", "IQ categories + streaks", "AI decision analysis"] },
  { name: "ACE", price: "$8/mo", accent: "#FACC15", perks: ["25 scenarios per day", "Full Levels 1–5 (Pro/GOAT IQ)", "Adaptive difficulty engine", "Weakness detection + retesting", "AI scenario variations", "Weekly tactical report"] },
];

function SampleQuestion({ q, onAnswered }) {
  const [choice, setChoice] = useState(null);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/brain/public/answer", { n: q.n, choice });
      setRes(data); onAnswered();
    } catch {} finally { setBusy(false); }
  };
  return (
    <div className="bg-pb-surface border border-cyan-400/30 p-6" data-testid={`sample-q-${q.n}`}>
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[9px] font-black uppercase tracking-[0.25em] text-cyan-300 border border-cyan-400/50 px-2 py-0.5">{q.level_name}</span>
        <span className="text-[9px] uppercase tracking-[0.2em] text-zinc-500">{q.categories.join(" · ")}</span>
      </div>
      <div className="font-display font-black uppercase text-xl text-cyan-300 mb-2">{q.title}</div>
      <p className="text-sm text-zinc-300 border-l-2 border-cyan-400/50 pl-3 mb-3">{q.scenario}</p>
      <p className="font-bold text-sm mb-3">{q.question}</p>
      {!res ? (
        <>
          <div className="space-y-2 mb-4">
            {q.options.map((o, i) => (
              <button key={i} data-testid={`sample-opt-${q.n}-${i}`} onClick={() => setChoice(i)}
                className={`w-full text-left px-3 py-2.5 border text-sm transition-all ${choice === i ? "border-cyan-400 bg-cyan-400/10 text-cyan-200" : "border-pb-border text-zinc-300 hover:border-zinc-500"}`}>
                <span className="font-black text-cyan-400 mr-2">{String.fromCharCode(65 + i)}.</span>{o}
              </button>
            ))}
          </div>
          <button data-testid={`sample-submit-${q.n}`} disabled={choice == null || busy} onClick={submit}
            className="bg-cyan-400 text-black px-5 py-2.5 text-[10px] font-black uppercase tracking-widest hover:bg-cyan-300 disabled:opacity-40 transition-colors">Lock In Decision</button>
        </>
      ) : (
        <div className={`border p-4 ${res.correct ? "border-lime-400/50 bg-lime-400/5" : "border-red-400/50 bg-red-400/5"}`} data-testid={`sample-result-${q.n}`}>
          <div className="flex items-center gap-2 mb-2">
            {res.correct ? <Check className="w-5 h-5 text-lime-400" /> : <X className="w-5 h-5 text-red-400" />}
            <span className="font-display font-black uppercase" style={{ color: res.correct ? "#A3E635" : "#F87171" }}>
              {res.correct ? "Sharp read" : "Not quite"} · Decision IQ {res.score}
            </span>
          </div>
          <p className="text-xs text-zinc-300 mb-1"><span className="text-lime-400 font-bold uppercase text-[10px] tracking-wider">Solution: </span>{res.correct_solution}</p>
          <p className="text-xs text-zinc-400">{res.why}</p>
        </div>
      )}
    </div>
  );
}

export default function BrainPublic() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [answered, setAnswered] = useState(0);

  useEffect(() => {
    document.title = "Pickleball Brain Training — Test Your Pickleball IQ | Pickle Bullpen";
    api.get("/brain/public/sample").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  return (
    <div data-testid="brain-public-page" className="min-h-screen bg-pb-bg text-white">
      <div className="max-w-5xl mx-auto px-4 py-12">
        <div className="mb-10 animate-fade-up">
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-pb-cyan mb-2 flex items-center gap-2">
            <Brain className="w-4 h-4" /> PBPA · Court Mentality Training Hub
          </div>
          <h1 className="font-display font-black uppercase tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-none">
            What's Your <span className="text-cyan-300">Pickleball IQ</span>?
          </h1>
          <p className="text-zinc-400 mt-4 max-w-2xl text-sm md:text-base">
            This is not trivia. Brain Training drops you into real court situations — score, positioning, ball height, partner, opponent tendencies — and an AI tactician grades every decision you make across 10 dimensions. 100+ scenarios from Beginner IQ to GOAT IQ.
          </p>
          <div className="flex flex-wrap gap-6 mt-6 text-[10px] uppercase tracking-[0.2em] text-zinc-500">
            <span className="flex items-center gap-1.5"><Target className="w-4 h-4 text-cyan-300" /> {data?.total_bank || 100}+ scenarios</span>
            <span className="flex items-center gap-1.5"><Zap className="w-4 h-4 text-amber-400" /> AI decision grading</span>
            <span className="flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-lime-400" /> Weakness → drill loop</span>
          </div>
        </div>

        <div className="text-[10px] font-black uppercase tracking-[0.3em] text-zinc-500 mb-4">Try it — read the court and decide</div>
        <div className="grid md:grid-cols-1 gap-5 mb-6">
          {data?.questions?.slice(0, answered >= 1 ? 3 : 1).map((q) => (
            <SampleQuestion key={q.n} q={q} onAnswered={() => setAnswered((a) => a + 1)} />
          ))}
          {!data && <div className="text-zinc-500 text-sm">Loading scenarios…</div>}
        </div>
        {answered >= 1 && (
          <div data-testid="brain-cta" className="border border-cyan-400/50 bg-cyan-400/5 p-6 mb-12 flex flex-wrap items-center gap-4 animate-fade-up">
            <Brain className="w-8 h-8 text-cyan-300 shrink-0" />
            <div className="flex-1 min-w-[240px]">
              <div className="font-display font-black uppercase text-xl">Your full Brain IQ profile is waiting</div>
              <p className="text-sm text-zinc-400 mt-1">Members get a living Brain IQ score, 15 tactical category ratings, adaptive difficulty, weakness detection and drills matched to what the AI finds in your game.</p>
            </div>
            <button data-testid="brain-join-btn" onClick={() => navigate("/auth")}
              className="flex items-center gap-2 bg-cyan-400 text-black px-6 py-3 text-[11px] font-black uppercase tracking-widest hover:bg-cyan-300 transition-colors">
              Join The Bullpen — Free <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}

        <div className="text-[10px] font-black uppercase tracking-[0.3em] text-zinc-500 mb-4">Membership Access</div>
        <div className="grid sm:grid-cols-3 gap-4 mb-12">
          {TIERS.map((t) => (
            <div key={t.name} className="bg-pb-surface border p-5" style={{ borderColor: `${t.accent}55` }}>
              <div className="flex items-baseline justify-between mb-3">
                <span className="font-display font-black uppercase text-xl" style={{ color: t.accent }}>{t.name}</span>
                <span className="text-xs text-zinc-500 font-mono">{t.price}</span>
              </div>
              <ul className="space-y-1.5">
                {t.perks.map((p, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-zinc-300"><Check className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: t.accent }} />{p}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="text-center pb-8">
          <button onClick={() => navigate("/auth")} className="bg-cyan-400 text-black px-8 py-3.5 text-[11px] font-black uppercase tracking-widest hover:bg-cyan-300 transition-colors">
            Start Training Your Brain
          </button>
        </div>
      </div>
    </div>
  );
}
